import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Email API
export const emailApi = {
  // Get emails by folder
  getEmails: async (folder = 'inbox', search = '', skip = 0, limit = 50) => {
    const params = new URLSearchParams({ folder, skip: skip.toString(), limit: limit.toString() });
    if (search) params.append('search', search);
    const response = await axios.get(`${API}/emails/?${params}`);
    return response.data;
  },

  // Get folder counts
  getFolderCounts: async () => {
    const response = await axios.get(`${API}/emails/counts`);
    return response.data;
  },

  // Get single email
  getEmail: async (emailId) => {
    const response = await axios.get(`${API}/emails/${emailId}`);
    return response.data;
  },

  // Send email
  sendEmail: async (emailData) => {
    const response = await axios.post(`${API}/emails/send`, emailData);
    return response.data;
  },

  // Save draft
  saveDraft: async (emailData) => {
    const response = await axios.post(`${API}/emails/draft`, emailData);
    return response.data;
  },

  // Update email (read, starred, folder, labels)
  updateEmail: async (emailId, updateData) => {
    const response = await axios.patch(`${API}/emails/${emailId}`, updateData);
    return response.data;
  },

  // Delete email
  deleteEmail: async (emailId, permanent = false) => {
    const response = await axios.delete(`${API}/emails/${emailId}?permanent=${permanent}`);
    return response.data;
  },

  // Bulk move
  bulkMove: async (emailIds, folder) => {
    const response = await axios.post(`${API}/emails/bulk/move`, { email_ids: emailIds, folder });
    return response.data;
  },

  // Bulk delete
  bulkDelete: async (emailIds, permanent = false) => {
    const response = await axios.post(`${API}/emails/bulk/delete`, { email_ids: emailIds, permanent });
    return response.data;
  },

  // Custom folders
  getCustomFolders: async () => {
    const response = await axios.get(`${API}/emails/folders/custom`);
    return response.data;
  },

  createCustomFolder: async (name, color = '#6D4AFF', icon = 'Folder') => {
    const response = await axios.post(`${API}/emails/folders/custom`, { name, color, icon });
    return response.data;
  },

  updateCustomFolder: async (folderId, data) => {
    const response = await axios.put(`${API}/emails/folders/custom/${folderId}`, data);
    return response.data;
  },

  deleteCustomFolder: async (folderId) => {
    const response = await axios.delete(`${API}/emails/folders/custom/${folderId}`);
    return response.data;
  }
};

// User Preferences API
export const preferencesApi = {
  // Get user preferences
  getPreferences: async () => {
    const response = await axios.get(`${API}/auth/user/preferences`);
    return response.data;
  },

  // Save user preferences
  savePreferences: async (preferences) => {
    const response = await axios.put(`${API}/auth/user/preferences`, preferences);
    return response.data;
  }
};

// Contacts API
export const contactsApi = {
  // Search contacts (other users in the system)
  search: async (query = '', limit = 20) => {
    const params = new URLSearchParams({ limit: limit.toString() });
    if (query) params.append('search', query);
    const response = await axios.get(`${API}/contacts/?${params}`);
    return response.data;
  },

  // Get all users
  getAll: async () => {
    const response = await axios.get(`${API}/contacts/all`);
    return response.data;
  },

  // Get user's personal contacts
  getContacts: async () => {
    const response = await axios.get(`${API}/contacts/list`);
    return response.data;
  },

  // Add a contact
  addContact: async (contactData) => {
    const response = await axios.post(`${API}/contacts/`, contactData);
    return response.data;
  },

  // Remove a contact
  removeContact: async (contactId) => {
    const response = await axios.delete(`${API}/contacts/${contactId}`);
    return response.data;
  }
};

// Labels API
export const labelsApi = {
  getAll: async () => {
    const response = await axios.get(`${API}/labels/`);
    return response.data;
  },

  create: async (name, color) => {
    const response = await axios.post(`${API}/labels/`, { name, color });
    return response.data;
  },

  update: async (labelId, name, color) => {
    const response = await axios.put(`${API}/labels/${labelId}`, { name, color });
    return response.data;
  },

  delete: async (labelId) => {
    const response = await axios.delete(`${API}/labels/${labelId}`);
    return response.data;
  }
};

// Admin API
export const adminApi = {
  // Get users list
  getUsers: async (params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.search) searchParams.append('search', params.search);
    if (params.role) searchParams.append('role', params.role);
    if (params.status) searchParams.append('status', params.status);
    if (params.skip) searchParams.append('skip', params.skip.toString());
    if (params.limit) searchParams.append('limit', params.limit.toString());
    const response = await axios.get(`${API}/admin/users?${searchParams}`);
    return response.data;
  },

  // Get user count
  getUsersCount: async () => {
    const response = await axios.get(`${API}/admin/users/count`);
    return response.data;
  },

  // Create user
  createUser: async (userData, role = 'user') => {
    const response = await axios.post(`${API}/admin/users?role=${role}`, userData);
    return response.data;
  },

  // Get user
  getUser: async (userId) => {
    const response = await axios.get(`${API}/admin/users/${userId}`);
    return response.data;
  },

  // Update user
  updateUser: async (userId, updateData) => {
    const response = await axios.patch(`${API}/admin/users/${userId}`, updateData);
    return response.data;
  },

  // Delete user
  deleteUser: async (userId) => {
    const response = await axios.delete(`${API}/admin/users/${userId}`);
    return response.data;
  },

  // Reset password
  resetPassword: async (userId, newPassword) => {
    const response = await axios.post(`${API}/admin/users/${userId}/reset-password?new_password=${encodeURIComponent(newPassword)}`);
    return response.data;
  },

  // Impersonate user (sign in as)
  impersonateUser: async (userId) => {
    const response = await axios.post(`${API}/admin/users/${userId}/impersonate`);
    return response.data;
  },

  // Get stats
  getStats: async () => {
    const response = await axios.get(`${API}/admin/stats`);
    return response.data;
  },

  // Invitations
  createInvitation: async (email) => {
    const response = await axios.post(`${API}/admin/invitations`, { email });
    return response.data;
  },

  getInvitations: async () => {
    const response = await axios.get(`${API}/admin/invitations`);
    return response.data;
  },

  deleteInvitation: async (invitationId) => {
    const response = await axios.delete(`${API}/admin/invitations/${invitationId}`);
    return response.data;
  },

  // Clear site cache
  clearSiteCache: async () => {
    const response = await axios.post(`${API}/admin/clear-cache`);
    return response.data;
  },

  // Get cache version
  getCacheVersion: async () => {
    const response = await axios.get(`${API}/admin/cache-version`);
    return response.data;
  }
};

// Plans API
export const plansApi = {
  // Get all plans (admin)
  getAll: async (includeInactive = true) => {
    const response = await axios.get(`${API}/plans/?include_inactive=${includeInactive}`);
    return response.data;
  },

  // Get active plans (public)
  getActive: async () => {
    const response = await axios.get(`${API}/plans/active`);
    return response.data;
  },

  // Get single plan
  get: async (planId) => {
    const response = await axios.get(`${API}/plans/${planId}`);
    return response.data;
  },

  // Create plan
  create: async (planData) => {
    const response = await axios.post(`${API}/plans/`, planData);
    return response.data;
  },

  // Update plan
  update: async (planId, planData) => {
    const response = await axios.put(`${API}/plans/${planId}`, planData);
    return response.data;
  },

  // Delete plan
  delete: async (planId) => {
    const response = await axios.delete(`${API}/plans/${planId}`);
    return response.data;
  },

  // Toggle active status
  toggleActive: async (planId) => {
    const response = await axios.post(`${API}/plans/${planId}/toggle-active`);
    return response.data;
  },

  // Duplicate plan
  duplicate: async (planId) => {
    const response = await axios.post(`${API}/plans/${planId}/duplicate`);
    return response.data;
  }
};

// Payment API
export const paymentApi = {
  // Get active plans (public)
  getActivePlans: async () => {
    const response = await axios.get(`${API}/payments/plans/active`);
    return response.data;
  },

  // Create checkout session
  createCheckout: async (planId, billingCycle, couponCode = null) => {
    const originUrl = window.location.origin;
    const payload = {
      plan_id: planId,
      billing_cycle: billingCycle,
      origin_url: originUrl
    };
    if (couponCode) {
      payload.coupon_code = couponCode;
    }
    const response = await axios.post(`${API}/payments/checkout`, payload);
    return response.data;
  },

  // Get checkout status
  getCheckoutStatus: async (sessionId, fromSuccess = false) => {
    const response = await axios.get(`${API}/payments/checkout/status/${sessionId}`, {
      params: fromSuccess ? { from_success: true } : {}
    });
    return response.data;
  }
};

// Messenger API
export const messengerApi = {
  // Search users
  searchUsers: async (email) => {
    const response = await axios.post(`${API}/messenger/search`, { email });
    return response.data;
  },

  // Friend requests
  sendFriendRequest: async (toEmail) => {
    const response = await axios.post(`${API}/messenger/friend-request`, { to_email: toEmail });
    return response.data;
  },

  getFriendRequests: async () => {
    const response = await axios.get(`${API}/messenger/friend-requests`);
    return response.data;
  },

  respondToFriendRequest: async (requestId, action) => {
    const response = await axios.post(`${API}/messenger/friend-request/respond`, { request_id: requestId, action });
    return response.data;
  },

  // Friends
  getFriends: async () => {
    const response = await axios.get(`${API}/messenger/friends`);
    return response.data;
  },

  removeFriend: async (friendshipId) => {
    const response = await axios.delete(`${API}/messenger/friends/${friendshipId}`);
    return response.data;
  },

  // Status
  updateStatus: async (status) => {
    const response = await axios.put(`${API}/messenger/status`, { status });
    return response.data;
  },

  getStatus: async () => {
    const response = await axios.get(`${API}/messenger/status`);
    return response.data;
  },

  // Messages
  sendMessage: async (toUserId, content, messageType = 'text') => {
    const response = await axios.post(`${API}/messenger/messages`, { 
      to_user_id: toUserId, 
      content, 
      message_type: messageType 
    });
    return response.data;
  },

  getMessages: async (friendId) => {
    const response = await axios.get(`${API}/messenger/messages/${friendId}`);
    return response.data;
  },

  getUnreadCount: async () => {
    const response = await axios.get(`${API}/messenger/unread-count`);
    return response.data;
  },

  // Meetings
  createMeeting: async (friendIds, title, scheduledTime, durationMinutes = 60) => {
    const response = await axios.post(`${API}/messenger/meetings`, {
      friend_ids: friendIds,
      title,
      scheduled_time: scheduledTime,
      duration_minutes: durationMinutes
    });
    return response.data;
  },

  getMeetings: async () => {
    const response = await axios.get(`${API}/messenger/meetings`);
    return response.data;
  },

  respondToMeeting: async (meetingId, response) => {
    const resp = await axios.post(`${API}/messenger/meetings/${meetingId}/respond?response=${response}`);
    return resp.data;
  }
};

// Coupon API
export const couponApi = {
  // Admin endpoints
  listCoupons: async () => {
    const response = await axios.get(`${API}/coupons/admin/list`);
    return response.data;
  },

  createCoupon: async (couponData) => {
    const response = await axios.post(`${API}/coupons/admin/create`, couponData);
    return response.data;
  },

  updateCoupon: async (couponId, updateData) => {
    const response = await axios.put(`${API}/coupons/admin/${couponId}`, updateData);
    return response.data;
  },

  deleteCoupon: async (couponId) => {
    const response = await axios.delete(`${API}/coupons/admin/${couponId}`);
    return response.data;
  },

  getCouponRedemptions: async (couponId) => {
    const response = await axios.get(`${API}/coupons/admin/${couponId}/redemptions`);
    return response.data;
  },

  // Public endpoints
  validateCoupon: async (code, planId = null, planPrice = null) => {
    const response = await axios.post(`${API}/coupons/validate`, { 
      code, 
      plan_id: planId, 
      plan_price: planPrice 
    });
    return response.data;
  },

  applyCoupon: async (code, planId = null, planPrice = null) => {
    const response = await axios.post(`${API}/coupons/apply`, { 
      code, 
      plan_id: planId, 
      plan_price: planPrice 
    });
    return response.data;
  },

  clearCoupon: async () => {
    const response = await axios.delete(`${API}/coupons/clear`);
    return response.data;
  }
};

// Broadcasts API
export const broadcastsApi = {
  // Get broadcasts for current user
  getMyBroadcasts: async (unreadOnly = false) => {
    const response = await axios.get(`${API}/broadcasts/my`, {
      params: { unread_only: unreadOnly }
    });
    return response.data;
  },

  // Get unread count
  getUnreadCount: async () => {
    const response = await axios.get(`${API}/broadcasts/unread-count`);
    return response.data;
  },

  // Mark broadcast as read
  markAsRead: async (broadcastId) => {
    const response = await axios.post(`${API}/broadcasts/${broadcastId}/read`);
    return response.data;
  },

  // Mark all as read
  markAllAsRead: async () => {
    const response = await axios.post(`${API}/broadcasts/mark-all-read`);
    return response.data;
  },

  dismiss: async (broadcastId) => {
    const response = await axios.post(`${API}/broadcasts/${broadcastId}/dismiss`);
    return response.data;
  }
};

// User Notifications API (support replies, etc.)
export const userNotificationsApi = {
  // Get notifications
  getNotifications: async (unreadOnly = false) => {
    const response = await axios.get(`${API}/support/notifications`, {
      params: { unread_only: unreadOnly }
    });
    return response.data;
  },

  // Get unread count
  getUnreadCount: async () => {
    const response = await axios.get(`${API}/support/notifications/unread-count`);
    return response.data;
  },

  // Mark notification as read
  markAsRead: async (notificationId) => {
    const response = await axios.post(`${API}/support/notifications/${notificationId}/read`);
    return response.data;
  },

  // Mark all as read
  markAllAsRead: async () => {
    const response = await axios.post(`${API}/support/notifications/mark-all-read`);
    return response.data;
  },

  // Dismiss a notification
  dismiss: async (notificationId) => {
    const response = await axios.delete(`${API}/support/notifications/${notificationId}`);
    return response.data;
  }
};


// Password Reset API
export const passwordResetApi = {
  // Request password reset email
  forgotPassword: async (email) => {
    const response = await axios.post(`${API}/auth/forgot-password`, { email });
    return response.data;
  },

  // Verify reset token is valid
  verifyToken: async (token) => {
    const response = await axios.get(`${API}/auth/verify-reset-token`, {
      params: { token }
    });
    return response.data;
  },

  // Reset password with token
  resetPassword: async (token, newPassword) => {
    const response = await axios.post(`${API}/auth/reset-password`, {
      token,
      new_password: newPassword
    });
    return response.data;
  }
};


// Wall API
export const wallApi = {
  // Check if user has wall access
  getAccess: async () => {
    const response = await axios.get(`${API}/walls/access`);
    return response.data;
  },

  // Get storage pricing
  getStoragePricing: async () => {
    const response = await axios.get(`${API}/walls/storage-pricing`);
    return response.data;
  },

  // Purchase wall storage
  purchaseStorage: async (originUrl) => {
    const response = await axios.post(`${API}/walls/purchase-storage`, null, {
      params: { origin_url: originUrl }
    });
    return response.data;
  },

  // Verify storage purchase
  verifyPurchase: async (sessionId) => {
    const response = await axios.post(`${API}/walls/verify-storage-purchase`, null, {
      params: { session_id: sessionId }
    });
    return response.data;
  },

  // Create wall
  createWall: async (wallData) => {
    const response = await axios.post(`${API}/walls/`, wallData);
    return response.data;
  },

  // Get my wall
  getMyWall: async () => {
    const response = await axios.get(`${API}/walls/my-wall`);
    return response.data;
  },

  // Update my wall
  updateMyWall: async (wallData) => {
    const response = await axios.put(`${API}/walls/my-wall`, wallData);
    return response.data;
  },

  // Get wall by slug
  getWallBySlug: async (slug) => {
    const response = await axios.get(`${API}/walls/w/${slug}`);
    return response.data;
  },

  // Discover walls
  discoverWalls: async (search = '', skip = 0, limit = 20) => {
    const response = await axios.get(`${API}/walls/discover`, {
      params: { search, skip, limit }
    });
    return response.data;
  },

  // Get subscription feed
  getFeed: async (skip = 0, limit = 20) => {
    const response = await axios.get(`${API}/walls/feed`, {
      params: { skip, limit }
    });
    return response.data;
  },

  // Follow a wall (no notifications)
  follow: async (slug) => {
    const response = await axios.post(`${API}/walls/w/${slug}/follow`, null, {
      params: { follow_type: 'follower' }
    });
    return response.data;
  },

  // Subscribe to wall (with notifications)
  subscribe: async (slug) => {
    const response = await axios.post(`${API}/walls/w/${slug}/follow`, null, {
      params: { follow_type: 'subscriber' }
    });
    return response.data;
  },

  // Unsubscribe/unfollow from wall
  unsubscribe: async (slug) => {
    const response = await axios.delete(`${API}/walls/w/${slug}/subscribe`);
    return response.data;
  },

  // Update follow type (switch between follower and subscriber)
  updateFollowType: async (slug, followType) => {
    const response = await axios.post(`${API}/walls/w/${slug}/follow`, null, {
      params: { follow_type: followType }
    });
    return response.data;
  },

  // Get my subscriptions
  getMySubscriptions: async () => {
    const response = await axios.get(`${API}/walls/my-subscriptions`);
    return response.data;
  },

  // Get wall posts
  getWallPosts: async (slug, skip = 0, limit = 20) => {
    const response = await axios.get(`${API}/walls/w/${slug}/posts`, {
      params: { skip, limit }
    });
    return response.data;
  },

  // Create post
  createPost: async (postData) => {
    const response = await axios.post(`${API}/walls/my-wall/posts`, postData);
    return response.data;
  },

  // Update post
  updatePost: async (postId, postData) => {
    const response = await axios.put(`${API}/walls/my-wall/posts/${postId}`, postData);
    return response.data;
  },

  // Delete post
  deletePost: async (postId) => {
    const response = await axios.delete(`${API}/walls/my-wall/posts/${postId}`);
    return response.data;
  },

  // Toggle like on post
  toggleLike: async (postId) => {
    const response = await axios.post(`${API}/walls/posts/${postId}/like`);
    return response.data;
  },

  // Upload file to wall
  uploadFile: async (file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await axios.post(`${API}/walls/my-wall/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (progressEvent) => {
        if (onProgress) {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percent);
        }
      }
    });
    return response.data;
  },

  // List wall files
  listFiles: async () => {
    const response = await axios.get(`${API}/walls/my-wall/files`);
    return response.data;
  },

  // Delete wall file
  deleteFile: async (fileId) => {
    const response = await axios.delete(`${API}/walls/my-wall/files/${fileId}`);
    return response.data;
  }
};


// Sentinel Mail API
export const sentinelApi = {
  create: async (data) => {
    const response = await axios.post(`${API}/sentinel/create`, data);
    return response.data;
  },
  list: async (type = null, status = null) => {
    const params = {};
    if (type) params.sentinel_type = type;
    if (status) params.status_filter = status;
    const response = await axios.get(`${API}/sentinel/list`, { params });
    return response.data;
  },
  update: async (id, data) => {
    const response = await axios.put(`${API}/sentinel/${id}`, data);
    return response.data;
  },
  cancel: async (id) => {
    const response = await axios.delete(`${API}/sentinel/${id}`);
    return response.data;
  },
};


